import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ieltsSpeakingApi, type SpeakingCueCard, type SpeakingCueCardInput } from '@/api/ielts';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTitle } from '@/components/mobile/MobileKit';
import { MoreButton } from '@/components/mobile/MoreButton';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * IELTS Speaking cue cards — admin/manager content management (Feature #3b).
 *
 * Mirrors AdminIeltsWritingPage / AdminMaterialsPage: a list of cards + a
 * create/edit modal. These cards are the Part 2 long-turn prompts. Reachable by
 * both `admin` and `manager` (content, not financial); the server enforces
 * RequireRole("admin","manager").
 */
export function AdminIeltsSpeakingPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<SpeakingCueCard | null>(null);
  const [sheetFor, setSheetFor] = useState<SpeakingCueCard | null>(null);
  const isMobile = useIsMobile();

  const list = useQuery({
    queryKey: ['admin', 'ielts', 'speaking-cue-cards'],
    queryFn: ieltsSpeakingApi.adminListCueCards,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'ielts', 'speaking-cue-cards'] });
  const remove = useMutation({
    mutationFn: (id: string) => ieltsSpeakingApi.adminDeleteCueCard(id),
    onSuccess: invalidate,
  });

  const cards = list.data ?? [];
  const askRemove = async (card: SpeakingCueCard) => {
    if (await ask({ message: t('ieltsAdmin.speaking.deleteConfirm', { title: card.title }), danger: true })) {
      remove.mutate(card.id);
    }
  };
  const modals = (
    <>
      {creatingOpen && <CueCardModal onClose={() => setCreatingOpen(false)} onSaved={invalidate} />}
      {editing && <CueCardModal card={editing} onClose={() => setEditing(null)} onSaved={invalidate} />}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.14) ─────────────────────────
  // Карточки → строки «название · тема · активно»; два значка без подписей → «⋯» с листом.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('ieltsAdmin.speaking.title')}
          subtitle={t('ieltsAdmin.speaking.subtitle')}
          right={<MAdd label={t('ieltsAdmin.speaking.createBtn')} onClick={() => setCreatingOpen(true)} />}
        />
        {remove.error && <p className="px-0.5 text-[13px] text-error">{humanizeError(remove.error)}</p>}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !cards.length ? (
          <MEmpty icon="mic" title={t('ieltsAdmin.speaking.noCards')} />
        ) : (
          <MBlock>
            {cards.map((card, i) => (
              <MRow
                key={card.id}
                wrap
                last={i === cards.length - 1}
                onClick={() => setEditing(card)}
                title={card.title}
                meta={[
                  card.topic ?? '',
                  card.is_active ? t('ieltsAdmin.speaking.statusActive') : t('ieltsAdmin.speaking.statusHidden'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                aside={<MoreButton onClick={() => setSheetFor(card)} />}
              />
            ))}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => setEditing(sheetFor) },
                  { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => void askRemove(sheetFor) },
                ]
              : []
          }
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('ieltsAdmin.speaking.title')}
        subtitle={t('ieltsAdmin.speaking.subtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('ieltsAdmin.speaking.createBtn')}
          </Button>
        }
      />

      {remove.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{humanizeError(remove.error)}</Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !cards.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('ieltsAdmin.speaking.noCards')}{' '}
          <button type="button" onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('ieltsAdmin.speaking.createFirst')}
          </button>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {cards.map((card) => (
            <li key={card.id}>
              <Card className="flex h-full flex-col">
                <div className="flex items-center gap-2">
                  <Pill variant={card.is_active ? 'success' : 'neutral'}>
                    {card.is_active ? t('ieltsAdmin.speaking.statusActive') : t('ieltsAdmin.speaking.statusHidden')}
                  </Pill>
                  {card.topic && <Pill variant="primary">{card.topic}</Pill>}
                </div>
                <CardTitle className="mt-3">{card.title}</CardTitle>
                <p className="mt-1 line-clamp-4 whitespace-pre-line text-sm text-on-surface-variant">
                  {card.cue_text}
                </p>
                <div className="mt-auto flex gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(card)}>
                    <span className="material-symbols-outlined">edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void askRemove(card)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {modals}
    </>
  );
}

function CueCardModal({
  card,
  onClose,
  onSaved,
}: {
  card?: SpeakingCueCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(card);
  const [data, setData] = useState<SpeakingCueCardInput>(
    card
      ? {
          title: card.title,
          cue_text: card.cue_text,
          topic: card.topic ?? '',
          is_active: card.is_active,
        }
      : { title: '', cue_text: '', topic: '', is_active: true },
  );

  const save = useMutation({
    mutationFn: () => {
      const payload: SpeakingCueCardInput = {
        title: data.title?.trim(),
        cue_text: data.cue_text?.trim(),
        topic: data.topic?.trim() || undefined,
        is_active: data.is_active,
      };
      return isEdit ? ieltsSpeakingApi.adminUpdateCueCard(card!.id, payload) : ieltsSpeakingApi.adminCreateCueCard(payload);
    },
    onSuccess: () => { onSaved(); onClose(); },
  });

  const canSave = !!data.title?.trim() && !!data.cue_text?.trim();
  const isMobile = useIsMobile();
  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полями, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
        {isEdit ? t('common.save') : t('ieltsAdmin.speaking.createBtn')}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('ieltsAdmin.speaking.editCard') : t('ieltsAdmin.speaking.newCard')}
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3">
        <Input
          label={t('ieltsAdmin.speaking.fieldTitle')}
          value={data.title ?? ''}
          onChange={(e) => setData({ ...data, title: e.target.value })}
        />
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {t('ieltsAdmin.speaking.fieldCueText')}
          </span>
          <textarea
            className="min-h-[180px] w-full rounded-button bg-surface-container-lowest p-4 text-on-surface shadow-soft transition focus:shadow-ambient focus:outline-none"
            placeholder={t('ieltsAdmin.speaking.fieldCueTextHint')}
            value={data.cue_text ?? ''}
            onChange={(e) => setData({ ...data, cue_text: e.target.value })}
          />
        </label>
        <Input
          label={t('ieltsAdmin.speaking.fieldTopic')}
          hint={t('ieltsAdmin.speaking.fieldTopicHint')}
          value={data.topic ?? ''}
          onChange={(e) => setData({ ...data, topic: e.target.value })}
        />
        <Select
          label={t('ieltsAdmin.speaking.fieldStatus')}
          value={data.is_active ? 'on' : 'off'}
          onChange={(e) => setData({ ...data, is_active: e.target.value === 'on' })}
          options={[
            { value: 'on', label: t('ieltsAdmin.speaking.statusActive') },
            { value: 'off', label: t('ieltsAdmin.speaking.statusHidden') },
          ]}
        />
        {save.error && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        {!canSave && <p className="text-xs text-on-surface-variant">{t('ieltsAdmin.speaking.validationHint')}</p>}

        {!isMobile && buttons}
      </div>
    </Modal>
  );
}
